/**
 * Whether the public Games page is shown in this environment — the Dev
 * Console's Games page switch (`games_page_enabled`, ADR-035). Every "Games"
 * link and the page itself ask this, so an environment where the switch is
 * off (prod, until it is turned on) shows no trace of the page.
 *
 * Not React Query, on purpose: the marketing site's header asks too, and that
 * page renders with no query client (and, as a static snapshot, no script).
 * One request per page load, shared by every caller; `null` until it answers,
 * and on a server render. A failed request reads as off — a link to a page
 * that may not be there is worse than no link.
 */
import { useEffect, useSyncExternalStore } from 'react';
import api from './api';
import { arrivedWithGamesSnapshot } from './games-snapshot-data';

/** How long an answer is trusted before the next caller asks again. */
const FRESH_MS = 5 * 60 * 1000;

/**
 * A page that arrived as a Games snapshot starts as "shown": the publisher
 * writes snapshots only while the switch is on and withdraws them when it is
 * turned off (ADR-036). It is still confirmed with the server on mount — the
 * answer counts as never fetched.
 */
function initialAnswer(): boolean | null {
  return arrivedWithGamesSnapshot() ? true : null;
}

let enabled: boolean | null = initialAnswer();
let fetchedAt = 0;
let inFlight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function publish(next: boolean): void {
  enabled = next;
  fetchedAt = Date.now();
  for (const listener of listeners) listener();
}

/** Ask the server again (after the switch is flipped, or when the answer is stale). */
export function refreshGamesAvailability(): Promise<void> {
  if (!inFlight) {
    // Started inside a promise so that even a throw on the way in reads as off.
    inFlight = Promise.resolve()
      .then(() => api.getGamesAvailability())
      .then((r) => publish(r?.enabled === true))
      .catch(() => publish(false))
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** True when the Games page is shown; false when it is not; null until known. */
export function useGamesPageAvailable(): boolean | null {
  const value = useSyncExternalStore(
    subscribe,
    () => enabled,
    () => null,
  );
  useEffect(() => {
    if (enabled === null || Date.now() - fetchedAt > FRESH_MS) void refreshGamesAvailability();
  }, []);
  return value;
}

/** Test hook. */
export function __resetGamesAvailabilityForTests(): void {
  enabled = initialAnswer();
  fetchedAt = 0;
  inFlight = null;
  listeners.clear();
}
